﻿import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; 


import Modal from 'bootstrap/js/dist/modal'; 

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import TileGridWMTS from 'ol/tilegrid/WMTS';
import { Select } from 'ol/interaction';
import { click } from 'ol/events/condition';
import GeoJSON from 'ol/format/GeoJSON';
import { defaults as defaultInteractions } from 'ol/interaction';

import { getTopLeft } from 'ol/extent';

import { getStartMap, getAllPoints, GetLayerCapabilitiesResponse, GetAllPointsResponse, getAllFeedback } from './services/network';
import { FeedbackDto } from './features/map/commands/sendFeedback/feedbackDto';

/* Stuff for adding points to map */
import { registerFeedbackClick } from './features/map/commands/sendFeedback/registerFeedbackClick';

import { initDynamicSurveyDialog } from './features/map/components/dynamicSurveyDialog';
import { showDialogInfoBox, registerDialogInfoButton } from './features/map/components/dialogInfoBox';
import { getContactSettings } from './features/map/components/contactDetails';
import { getDialogConfig, DialogConfig } from './services/network';

import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Text, Fill, Stroke } from 'ol/style';

import { initProjections, extent3006, resolutions3006 } from './shared/projections';

// Initialize projections on module load
initProjections();


const fallbackResolutions = resolutions3006;
const fallbackMatrixIds = Array.from({length: 21}, (_, i) => `Sweref 99 TM:${i}`);
const fallbackLayerName = 'Projektkarta_V2';

window.onload = async function() {
    await initMap();
}

let map: Map;

/** The dialog's public settings - missing on the old Index page (no slug). */
let dialogSettings: DialogConfig | null = null;

async function initMap() {
    const slug = (window as any).dialogSlug;
    let dialogConfig: DialogConfig | null = null;

    // Load dialog config (fast local call) if a slug exists
    if (slug) {
        try {
            dialogConfig = await getDialogConfig(slug);
            dialogSettings = dialogConfig;
        } catch (error) {
            console.error('Failed to load dialog config:', error);
        }
    }

    // Use dialog config for center/zoom if available
    const center = dialogConfig
        ? [dialogConfig.centerX, dialogConfig.centerY]
        : [743713.2891424781, 7064169.248631525];
    const zoom = dialogConfig?.zoom || 14.5;
    const layerName = dialogConfig?.layerName || fallbackLayerName;

    // Create the map immediately with fallback values (does not block on the WMS server)
    const tileSource = new TileWMS({
        url: 'https://wms.umea.se/geoserver/gwc/service/wms',
        tileGrid: new TileGridWMTS({
            origin: getTopLeft(extent3006),
            resolutions: fallbackResolutions,
            matrixIds: fallbackMatrixIds,
        }),
        params: {
            'LAYERS': layerName,
            'FORMAT': 'image/png',
            'TILED': true,
            'VERSION': '1.1.0',
            'WIDTH': 256,
            'HEIGHT': 256,
            'SRS': 'EPSG:3006'
        },
        hidpi: false,
        serverType: 'geoserver'
    });

    map = new Map({
        view: new View({
            extent: extent3006,
            center: center,
            zoom: zoom,
            projection: 'EPSG:3006',
            resolutions: fallbackResolutions,
        }),
        layers: [
            new TileLayer({
                extent: extent3006,
                preload: 0,
                visible: true,
                zIndex: -1,
                source: tileSource
            })
        ],
        target: 'map',
        controls: [],
        interactions: defaultInteractions({ altShiftDragRotate: false, pinchRotate: false })
    });

    const contactSettings = getContactSettings(dialogSettings);

    // A closed dialog (before start date or after end date) accepts no new points
    const isOpen = dialogSettings ? dialogSettings.isOpen : true;
    if (isOpen) {
        registerFeedbackClick(map, {
            allowRating: dialogSettings ? dialogSettings.allowRating : true,
            showFeedbackToPublic: dialogSettings ? dialogSettings.showFeedbackToPublic : true,
            contact: contactSettings
        });
    }

    (window as any).map = map;

    // Run everything asynchronously in parallel — nothing blocks the map
    const safeRun = (fn: () => Promise<void>) => fn().catch(e => console.error(e));

    const tasks: Promise<void>[] = [
        safeRun(() => loadCapabilitiesAsync(tileSource, layerName))
    ];

    if (!dialogSettings || dialogSettings.showFeedbackToPublic) {
        tasks.push(safeRun(() => addFeedbackPoints()));
    }

    if (slug) {
        tasks.push(safeRun(() => initDynamicSurveyDialog(isOpen, contactSettings)));
    }

    if (dialogSettings?.showInfoBox) {
        registerDialogInfoButton();
        showDialogInfoBox();
    }

    await Promise.all(tasks);
}

async function loadCapabilitiesAsync(tileSource: TileWMS, layerName: string): Promise<void> {
    try {
        // Fetch capabilities for the dialog's own layer, not the default layer in startmap.json
        const startMapData = await getStartMap(layerName);

        // Update the tile grid with correct resolutions/matrixIds from the server
        tileSource.setTileGridForProjection(
            'EPSG:3006',
            new TileGridWMTS({
                origin: getTopLeft(extent3006),
                resolutions: startMapData.resolutions,
                matrixIds: startMapData.matrixIds,
            })
        );

        tileSource.refresh();
    } catch (error) {
        console.warn('Kunde inte ladda capabilities från server, använder fallback-värden:', error);
    }
}


async function addPoints() {
    const pointsResponse: GetAllPointsResponse = await getAllPoints();


    const features = pointsResponse.points.map((pointDto, index) => {

        const coordinates = [pointDto.x, pointDto.y];
        const epsgCode = pointDto.epsgCode ? `EPSG:${pointDto.epsgCode}` : 'EPSG:4326';



        // Construct point feature and add to map
        const point = new Feature({
            geometry: new Point(coordinates).transform(epsgCode, 'EPSG:3006')

        });


        return point;
        });

        const vectorSource = new VectorSource({
            features: features
           
        });

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            zIndex: 10 // Higher z-index so the points end up on top of the background map
           
        });


    map.addLayer(vectorLayer);

    // Add Select interaction to handle clicks on points
    const selectInteraction = new Select({
        condition: click,
        layers: [vectorLayer]
    });

    map.addInteraction(selectInteraction);

    // Listen for the select event
    selectInteraction.on('select', (event) => {

        const geojsonFormat = new GeoJSON();

        event.selected.forEach((feature) => {
            const geometry = feature.getGeometry();
            const json = geojsonFormat.writeGeometryObject(geometry);

            alert("GeoJSON:" + JSON.stringify(json, null, 2));
        });

     
    });

}

let feedbackPointsLayer: VectorLayer | null = null;

export async function addFeedbackPoints() {
    const feedbackPoints: FeedbackDto[] = await getAllFeedback();
    const features = feedbackPoints.map((point) => {
        const coordinates = point.coordinate;
        const epsgCode = point.epsg || 'EPSG:4326';
        const feature = new Feature({
            geometry: new Point(coordinates).transform(epsgCode, 'EPSG:3006')
        });
        feature.set('rating', point.rating); // Set rating as an attribute
        return feature;
    });
    const vectorSource = new VectorSource({ features });
    const vectorLayer = new VectorLayer({
        source: vectorSource,
        zIndex: 11,
        style: (feature) => {
            const rating = feature.get('rating');
            const color = ratingColor(rating);
            return new Style({
                text: new Text({
                    text: '•',
                    font: 'bold 48px sans-serif',
                    fill: new Fill({ color }),
                    stroke: new Stroke({ color: '#fff', width: 2 })
                })
            });
        }
    });
    map.addLayer(vectorLayer);
    feedbackPointsLayer = vectorLayer;
}

/** Green = good, red = bad, yellow = neutral (dialog without rating). */
function ratingColor(rating: string | undefined): string {
    switch (rating) {
        case 'up':
            return 'green';
        case 'down':
            return 'red';
        default:
            return '#f0ad00';
    }
}

export function removeFeedbackPointsLayer() {
    if (feedbackPointsLayer && map) {
        map.removeLayer(feedbackPointsLayer);
        feedbackPointsLayer = null;
    }
}
